import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Users, UserPlus, UserMinus, Search, Shield, Briefcase, CheckCircle2, Loader2 } from 'lucide-react';

/** ProjectMembersPage
 *  =================
 *  /members → admin surface that shows every project with its current member
 *  roster and gives admins a per-user include/exclude toggle.  Pairs with the
 *  backend GET/DELETE/POST on /api/projects/{id}/access endpoints.
 */
export default function ProjectMembersPage() {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    Promise.all([api.get('/projects'), api.get('/users')]).then(([p, u]) => {
      setProjects(p.data);
      setAllUsers(u.data);
      if (p.data.length) setProjectId(p.data[0].id);
    });
  }, []);

  const loadMembers = async () => {
    if (!projectId) return;
    const { data } = await api.get(`/projects/${projectId}/members`);
    setMembers(data.members || []);
  };
  useEffect(() => { loadMembers(); }, [projectId]);

  const grantedIds = new Set(members.filter(m => m.access_type === 'granted').map(m => m.id));
  const implicitIds = new Set(members.filter(m => m.access_type === 'implicit_via_tasks').map(m => m.id));

  const filteredUsers = allUsers.filter(u => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (u.email || '').toLowerCase().includes(q)
        || (u.full_name || '').toLowerCase().includes(q)
        || (u.role || '').toLowerCase().includes(q);
  });

  const grant = async (uid, role) => {
    setBusy(true); setToast('');
    try {
      await api.post(`/projects/${projectId}/grant-access`, { user_id: uid, role });
      setToast('Access granted');
      await loadMembers();
    } catch (e) { setToast(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const revoke = async (uid) => {
    setBusy(true); setToast('');
    try {
      const r = await api.delete(`/projects/${projectId}/access/${uid}`);
      setToast(r.data.open_tasks_remaining > 0
        ? `Removed grant. Note: user still has ${r.data.open_tasks_remaining} open task(s) — reassign them for a hard revoke.`
        : 'Removed from project');
      await loadMembers();
    } catch (e) { setToast(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const proj = projects.find(p => p.id === projectId);

  return (
    <div className="p-6 space-y-5" data-testid="project-members-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-400"/> Project Members
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">Include or exclude anyone — producers, supervisors, artists, vendors, freelancers — per project.</p>
        </div>
        <select value={projectId} onChange={e=>setProjectId(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="members-project">
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {toast && <div className="text-[12px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-3 py-2">{toast}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Current members */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="border-b border-zinc-800 bg-zinc-900/50 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <CheckCircle2 className="h-4 w-4 text-emerald-400"/>
              Current on <span className="text-amber-300">{proj?.name || '—'}</span>
            </div>
            <span className="text-[11px] text-zinc-500">{members.length} member{members.length!==1?'s':''}</span>
          </div>
          <div className="divide-y divide-zinc-900 max-h-[60vh] overflow-y-auto">
            {members.length === 0 && <div className="p-8 text-center text-xs text-zinc-500">No members yet.</div>}
            {members.map(m => (
              <div key={m.id} className="px-4 py-3 flex items-center gap-3" data-testid={`member-row-${m.id}`}>
                <div className="h-8 w-8 rounded-full bg-zinc-800 flex items-center justify-center text-[11px] font-bold text-zinc-300 shrink-0">
                  {(m.full_name || m.email || '?').slice(0,1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{m.full_name || '(no name)'}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.email} · {(m.role||'').replace('_',' ')}</div>
                </div>
                {m.access_type === 'granted'
                  ? <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 rounded px-1.5 py-0.5">granted</span>
                  : <span className="text-[10px] font-bold uppercase tracking-wider text-violet-300 bg-violet-500/10 border border-violet-500/40 rounded px-1.5 py-0.5" title="User has open tasks in this project so they still see it.">via tasks</span>
                }
                {m.access_type === 'granted' && (
                  <button onClick={()=>revoke(m.id)} disabled={busy}
                    className="ml-1 px-2 py-1 rounded bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/40 text-rose-300 text-[11px] font-semibold flex items-center gap-1"
                    data-testid={`revoke-${m.id}`}>
                    <UserMinus className="h-3 w-3"/> Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Add from user pool */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="border-b border-zinc-800 bg-zinc-900/50 px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <UserPlus className="h-4 w-4 text-amber-400"/>
              Add to {proj?.name || 'project'}
            </div>
            <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 flex-1 max-w-[220px]">
              <Search className="h-3 w-3 text-zinc-500 shrink-0"/>
              <input
                value={query}
                onChange={e=>setQuery(e.target.value)}
                placeholder="Search by name / email / role"
                className="bg-transparent text-xs text-white flex-1 outline-none min-w-0"
                data-testid="members-search"
              />
            </div>
          </div>
          <div className="divide-y divide-zinc-900 max-h-[60vh] overflow-y-auto">
            {filteredUsers.map(u => {
              const isGranted = grantedIds.has(u.id);
              const isImplicit = implicitIds.has(u.id);
              return (
                <div key={u.id} className="px-4 py-2 flex items-center gap-3" data-testid={`pool-row-${u.id}`}>
                  <RoleBadge role={u.role}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate">{u.full_name || u.email}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{u.email}</div>
                  </div>
                  {isGranted ? (
                    <span className="text-[10px] text-emerald-400">✓ on project</span>
                  ) : isImplicit ? (
                    <button onClick={()=>grant(u.id, u.role)} disabled={busy}
                      className="px-2 py-1 rounded bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/40 text-violet-300 text-[10px] font-semibold"
                      data-testid={`grant-${u.id}`}>
                      Lock in
                    </button>
                  ) : (
                    <button onClick={()=>grant(u.id, u.role)} disabled={busy}
                      className="px-2 py-1 rounded bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-zinc-900 text-[10px] font-bold flex items-center gap-1"
                      data-testid={`grant-${u.id}`}>
                      {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <UserPlus className="h-3 w-3"/>} Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const r = (role || '').toLowerCase();
  const Icon = ['master_admin','production_manager','supervisor','hr_manager'].includes(r) ? Shield : Briefcase;
  return (
    <div className="h-7 w-7 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
      <Icon className="h-3 w-3 text-zinc-400"/>
    </div>
  );
}
